import { useState, useEffect } from "react";
import { Activity, Zap, RefreshCw } from "lucide-react";
import axios, { API } from "@/utils/api";
import { useT, useDeptName } from "@/contexts/LanguageContext";

const RES_DEPTS = [
  { key: "res_hvac", name: "HVAC Systems" },
  { key: "res_insulation", name: "Insulation & Envelope" },
  { key: "res_lighting", name: "Lighting" },
  { key: "res_appliances", name: "Appliances" },
  { key: "res_water_heating", name: "Water Heating" },
];

const ResidentialIoTView = ({ clientId }) => {
  const t = useT();
  const dn = useDeptName();
  const [readings, setReadings] = useState([]);
  const [simulating, setSimulating] = useState(false);
  const [selectedDept, setSelectedDept] = useState("res_hvac");

  const fetchReadings = async () => {
    try {
      const response = await axios.get(`${API}/clients/${clientId}/residential/iot/readings?limit=200`);
      setReadings(response.data);
    } catch (error) {
      console.error("Error fetching residential IoT:", error);
    }
  };

  useEffect(() => { fetchReadings(); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSimulate = async () => {
    setSimulating(true);
    try {
      await axios.post(`${API}/clients/${clientId}/residential/iot/simulate`, { department: selectedDept });
      await fetchReadings();
    } catch (error) {
      console.error("Error simulating:", error);
    } finally {
      setSimulating(false);
    }
  };

  const filteredReadings = selectedDept === "all"
    ? readings
    : readings.filter(r => r.department === selectedDept);

  return (
    <div data-testid="residential-iot-view">
      <div className="page-header">
        <h2 className="page-title">{t("residential_iot")}</h2>
        <p className="page-subtitle">Monitor and simulate IoT sensor readings for residential categories.</p>
      </div>

      <div className="card mb-lg">
        <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
            <select
              className="form-input"
              value={selectedDept}
              onChange={(e) => setSelectedDept(e.target.value)}
              style={{ width: "auto", minWidth: "180px" }}
              data-testid="res-iot-dept-select"
            >
              <option value="all">All Categories</option>
              {RES_DEPTS.map(d => (
                <option key={d.key} value={d.key}>{dn(d.key)}</option>
              ))}
            </select>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button className="btn btn-primary btn-sm" onClick={handleSimulate} disabled={simulating || selectedDept === "all"} data-testid="res-iot-simulate-btn">
              {simulating ? <><div className="spinner" style={{ width: "14px", height: "14px" }}></div> Simulating...</> : <><Zap size={16} /> Simulate IoT</>}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={fetchReadings} data-testid="res-iot-refresh-btn">
              <RefreshCw size={16} /> Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h4 className="card-title">
            <Activity size={18} style={{ marginRight: "8px", verticalAlign: "middle" }} />
            IoT Readings ({filteredReadings.length})
          </h4>
        </div>
        {filteredReadings.length === 0 ? (
          <div className="flex-center" style={{ padding: "var(--space-xxl)", color: "var(--text-muted)" }}>
            <div style={{ textAlign: "center" }}>
              <Activity size={48} strokeWidth={1} />
              <p className="mt-md">No IoT readings yet. Simulate data for a category to get started.</p>
            </div>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Sensor Type</th>
                  <th>Value</th>
                  <th>Unit</th>
                  <th>Source</th>
                  <th>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {filteredReadings.map((r, idx) => (
                  <tr key={r.id || idx}>
                    <td><span className="badge badge-neutral">{dn(r.department)}</span></td>
                    <td>{r.sensor_type?.replace(/_/g, " ")}</td>
                    <td style={{ fontWeight: "600" }}>{typeof r.value === "number" ? r.value.toFixed(2) : r.value}</td>
                    <td>{r.unit}</td>
                    <td><span className="badge badge-info">{r.source}</span></td>
                    <td className="text-muted">{r.timestamp ? new Date(r.timestamp).toLocaleString() : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResidentialIoTView;
